/**
 * DynamicTable V2 — external (consumer-authored) and internal (normalized runtime) schema types.
 *
 * External types are optional where authors omit fields; internal types require every field,
 * using `null` or empty collections for absence.
 */

// ---------------------------------------------------------------------------
// Column & primitive unions
// ---------------------------------------------------------------------------

export type ColumnType =
  | 'text'
  | 'html'
  | 'date'
  | 'dateTime'
  | 'dateTimeLTS'
  | 'dateRange'
  | 'conditionalDate'
  | 'number'
  | 'currency'
  | 'percentage'
  | 'badge'
  | 'couple'
  | 'coupleWithDateRange'
  | 'link'
  | 'modal'
  | 'mask'
  | 'sensitiveReveal'
  | 'conditional'
  | 'space'
  | 'time'
  | 'arraySplit'
  | 'custom';

/** Underlying cell value shape for validation and editing. */
export type DataType = 'string' | 'number' | 'date' | 'boolean' | 'object' | 'array';

/** Runtime allowlist for unknown-column-property warnings (must match {@link ExternalColumnDef} keys). */
export const EXTERNAL_COLUMN_DEF_KEYS = new Set<string>([
  'field',
  'header',
  'description',
  'type',
  'dataType',
  'width',
  'align',
  'sortable',
  'visibility',
  'group',
  'emptyDisplay',
  'prefix',
  'uppercase',
  'aggregate',
  'hideOnSubRows',
  'fixed',
  'badge',
  'couple',
  'link',
  'mask',
  'sensitiveReveal',
  'modal',
  'dateRange',
  'conditionalValues',
  'format',
  'fields',
  'editable',
  'clearOnEdit',
  'dataSource',
  'renderer',
  'actionIntent',
  'conditionalDisplay',
]);

// ---------------------------------------------------------------------------
// External schema — top level & metadata
// ---------------------------------------------------------------------------

/** Root schema object supplied by consumers or backend. */
export interface ExternalTableSchema {
  metadata?: TableMetadata;
  dataSource?: DataSourceConfig;
  columns: ExternalColumnDef[];
  actions?: ActionConfig;
  features?: FeaturesConfig;
  computeRules?: ComputeRule[];
  /** @deprecated Normalizer may read for legacy adapters. */
  options?: unknown;

  /**
   * When present and non-empty, the table renders as an accordion group —
   * shared page controls above collapsible sections, each containing a child table.
   * Root-level `columns` / `dataSource` / `actions` / `features` act as defaults
   * that sections inherit when they omit a field.
   */
  sections?: SectionDef[];
  /** Page-level controls (search, filters, toolbar, import) shared across all sections. */
  sharedControls?: SharedControlsDef;
  /** @deprecated Use `features.rows.styleRules` (declarative) or `features.rows.styleFn` (imperative) instead. */
  rowStyle?: (row: Record<string, unknown>, index: number) => Record<string, string | number> | undefined;
  /** Key into the global PluginRegistryMap for domain-specific cell renderers/editors. Resolved at render time. */
  plugins?: string;
}

export interface TableMetadata {
  title?: string;
  entity?: string;
  /** Optional documentation field. Not used at runtime. */
  description?: string;
}

/** Confirmation dialog config reused by dataSource save flow (`confirmBeforeSave`). */
export interface ConfirmConfig {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  titleAlign?: 'left' | 'center' | 'right';
  messageAlign?: 'left' | 'center' | 'right';
}

/**
 * Dialog shown **before** a row/bulk/toolbar action runs (distinct from `dataSource.confirmBeforeSave`).
 * - `kind: "confirm"` (default) — cancel + primary.
 * - `kind: "alert"` — single primary (acknowledge then proceed).
 */
export type ActionDialogKind = 'confirm' | 'alert';

export interface ActionDialogConfig {
  kind?: ActionDialogKind;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

/** @deprecated Use `ActionDialogKind`. */
export type PreflightDialogKind = ActionDialogKind;
/** @deprecated Use `ActionDialogConfig`. */
export type PreflightDialogConfig = ActionDialogConfig;

/**
 * Shown after a `"backgroundJob"` action completes when the API returns
 * `{ sentToBackground: true, taskReferenceNo: "..." }`.
 *
 * The built-in modal always shows a single primary button. All fields support
 * standard template tokens (`{response.taskReferenceNo}`, `{count}`, etc.).
 *
 * **Navigation field names elsewhere in actions** (each is a different feature):
 * - In-app route from a toolbar/row/bulk **button**: `actionType: "navigate"` uses **`path`**
 *   (and optional **`state`** for `navigate(path, { state })`).
 * - External URL in a **new tab** from a button: `actionType: "newTab"` uses **`url`**
 *   (or **`endpoint`** as the URL template).
 * - **After** a successful `"direct"` call, open a tab: `onSuccess: "openTab"` uses **`responseUrl`**
 *   (not `path` / not `navigatePath`).
 * - **After** the background-job modal primary click: this object’s **`navigatePath`** only
 *   (same app router as `navigate` actions when `DynamicTableV2` receives `navigate`).
 */
export interface BackgroundJobConfig {
  /** Dialog title. Default: `"Request Submitted"`. */
  title?: string;
  /** Dialog message. Default: `"Your request has been submitted for background processing."`. */
  message?: string;
  /** Primary button label. Default: `"View Details"`. */
  confirmLabel?: string;
  /**
   * In-app path when the user clicks the primary button (same as `navigate(path)` for
   * `actionType: "navigate"`). Supports `{response.taskReferenceNo}` and all standard row tokens.
   * If omitted the button only closes the dialog.
   */
  navigatePath?: string;
  /**
   * Whether to show the task reference number below the message.
   * Default: `true` when the API returns a non-empty `taskReferenceNo`.
   */
  showTaskReferenceNo?: boolean;
}

/**
 * For `loadMore` / `infiniteScroll`, some APIs need a **continuation query param** derived from the
 * last row of the accumulated list (e.g. `DateTime` = last item's `createdDate`).
 * Omitted for offset-only APIs.
 */
export type ContinuationCursorFormat = 'preserve' | 'iso' | 'mmddyyyy';

export interface ContinuationCursorConfig {
  /** Query parameter name (e.g. `DateTime` for award-settlement dashboard). */
  queryKey: string;
  /** Dot path on each row, e.g. `createdDate` or `nested.createdAt`. */
  fieldPath: string;
  /**
   * How to serialize the value for the query string.
   * - `preserve` — `String(value)` (default)
   * - `iso` — `moment(value).toISOString()` when valid
   * - `mmddyyyy` — `moment(value).format(DATE_FORMAT)` (MM/DD/YYYY) when valid
   */
  format?: ContinuationCursorFormat;
}

/** @deprecated Use `ContinuationCursorConfig`. */
export type LoadMoreCursorConfig = ContinuationCursorConfig;
/** @deprecated Use `ContinuationCursorFormat`. */
export type LoadMoreCursorFormat = ContinuationCursorFormat;

/** Table-level remote data configuration. */
export interface DataSourceConfig {
  endpoint: string;
  method?: 'GET' | 'POST';
  rowIdField: string;
  /**
   * Maps response fields to table data.
   * Defaults: `items: "items"`, `totalCount: "totalCount"`, `hasMoreItems: "hasMoreItems"`.
   * The table auto-detects both response formats:
   *   - Standard: `{ items: [...], totalCount: N }` — hasMore = loaded < totalCount
   *   - Cursor-based: `{ items: [...], hasMoreItems: true }` — hasMore = hasMoreItems flag
   * Only specify when the API uses non-standard field names.
   */
  responseMapping?: { items?: string; totalCount?: string; hasMoreItems?: string };
  /**
   * Maps query parameter names to internal table tokens.
   * Defaults to the standard Wealthlane params:
   *   `{ Filter: "table.searchText", MaxResultCount: "table.pageSize", SkipCount: "table.pageOffset", Sorting: "table.sorting" }`
   * Only specify when the API uses different parameter names.
   */
  requestParams?: Record<string, string>;
  /** Registered via `registerItemsTransform` — applied to list items after fetch. */
  itemsTransformId?: string;
  /**
   * Static query params always sent with list requests and included in `{tableQuery}` for bulk actions.
   * User-applied filters override keys with the same name.
   */
  staticQueryParams?: Record<string, unknown>;
  /**
   * When `features.pagination.mode` is `loadMore` or `infiniteScroll`, append this query param
   * on page ≥ 1 using the **last accumulated row**'s `fieldPath` value (same for button load-more and infinite scroll).
   */
  continuationCursor?: ContinuationCursorConfig | null;
  /** @deprecated Use `continuationCursor` (same shape). Still read by the normalizer for backward compatibility. */
  loadMoreCursor?: ContinuationCursorConfig | null;
  /**
   * Controls when the table fetches data.
   * - `'always'` (default) — fetches on mount and whenever params change.
   * - `'requireFilter'` — table stays empty until user applies a search or filter.
   */
  fetchPolicy?: 'always' | 'requireFilter';
  filters?: FilterDef[];
  saveEndpoint?: string;
  saveMethod?: 'POST' | 'PUT' | 'PATCH';
  onSaveSuccess?: 'refetch' | 'toast' | 'toastAndRefetch' | 'none';
  saveSuccessMessage?: string;
  confirmBeforeSave?: ConfirmConfig;
  /**
   * Maps row fields to payload fields before sending to `saveEndpoint`.
   * Keys = payload field names, values = row field names.
   * When set, only the mapped fields are included in the payload (acts as a whitelist + rename).
   *
   * ```ts
   * savePayloadMapper: { vestingDateId: 'rsuVestingDateId', settlementTypeId: 'settlementTypeId' }
   * // row.rsuVestingDateId → payload.vestingDateId
   * ```
   */
  savePayloadMapper?: Record<string, string>;
  /**
   * Routes inline-edit save to an OOP action class instead of a raw endpoint.
   * The action receives merged edited rows via `ctx.editedRows`.
   * Takes priority over `saveEndpoint` but NOT over the `onSave` component prop.
   *
   * The action class handles payload transformation, API calls, confirm dialogs,
   * and success/error feedback — full control, zero Page state.
   *
   * ```ts
   * saveActionId: 'SAVE_TRADE_ORDER'
   * ```
   */
  saveActionId?: string;
  /**
   * Maps URL path-token names → `context` prop key names for endpoint template substitution.
   * Token names must match `{Token}` placeholders in `endpoint` exactly (case-sensitive).
   *
   * ```ts
   * endpoint: '/api/customer/.../history/{ParticipantBrokerAccountId}',
   * pathParams: { ParticipantBrokerAccountId: 'participantBrokerAccountId' },
   * // Pass `context={{ participantBrokerAccountId: id }}` to DynamicTableV2.
   * ```
   */
  pathParams?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// External column
// ---------------------------------------------------------------------------

export interface ExternalColumnDef {
  field: string;
  header: string;
  /** If omitted, the normalizer generates a description from header + type. */
  description?: string;
  type?: ColumnType;
  dataType?: DataType;
  width?: number | { min?: number; max?: number; default?: number };
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  /**
   * Controls whether the column appears in the table and the column picker.
   *
   * - **omit** (default) — always visible, never in picker. The common case; write nothing.
   * - `"locked"`  — always visible, never in picker. Use for key identity columns
   *                 (e.g. participant name) that must never disappear.
   * - `"picker"`  — hidden by default; user can add it via the "Columns" picker.
   * - `"hidden"`  — never rendered, never in picker (helper / API-only fields).
   *
   * @deprecated `"mandatory"` → use `"locked"` | `"default"` → omit | `"optional"` → `"picker"` | `"internal"` → `"hidden"`
   */
  visibility?: 'locked' | 'picker' | 'hidden'
    // backwards-compat aliases accepted by the normalizer:
    | 'mandatory' | 'default' | 'optional' | 'internal';
  group?: string;
  /** Placeholder when the cell value is empty (normalized to `emptyValue` internally). */
  emptyDisplay?: string;
  prefix?: string;
  uppercase?: boolean;
  aggregate?: AggregateDef | string;
  hideOnSubRows?: boolean;
  fixed?: 'left' | 'right';
  badge?: BadgeConfig;
  couple?: CoupleConfig;
  link?: LinkConfig;
  mask?: MaskConfig;
  sensitiveReveal?: SensitiveRevealConfig;
  modal?: ModalConfig;
  dateRange?: { startField: string; endField: string };
  conditionalValues?: Record<string, string>;
  format?: FormatConfig;
  /**
   * When true, skips type-based format defaults (e.g. currency → 2dp) for this column.
   * Omit `format` to preserve API precision in the cell renderer.
   */
  skipFormatDefault?: boolean;
  fields?: Record<string, string | string[]>;
  editable?: EditableDef | boolean;
  clearOnEdit?: string[];
  dataSource?: DataSourceDef;
  /** Registered custom renderer id (maps to `customRenderer` internally). */
  renderer?: string;
  /** Makes the cell clickable — emits ActionRegistry.emitIntent() on click. */
  actionIntent?: ActionIntentConfig;
  /** Declarative conditional visual rules (strikethrough, badge, replacement, etc.) */
  conditionalDisplay?: ConditionalDisplayConfig;
}

/** Maps raw values to badge semantic variants for display. */
export interface BadgeConfig {
  statusMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default' | 'pink' | 'pink-outline' | 'primary' | 'primary-outline' | 'success-outline' | 'error-outline' | 'info-outline'>;
  displayMap?: Record<string, string>;
  defaultVariant?: 'success' | 'error' | 'warning' | 'info' | 'default' | 'pink' | 'pink-outline' | 'primary' | 'primary-outline' | 'success-outline' | 'error-outline' | 'info-outline';
  injected?: boolean;
}

export interface CoupleConfig {
  titleField?: string;
  subtitleField: string;
  showTag?: boolean;
}

export interface LinkConfig {
  url?: string;
  includeParams?: boolean;
  payloadMapper?: Array<{ labelKey: string; valueKey: string }>;
}

export interface MaskConfig {
  format: 'partial' | 'full';
  valueField: string;
}

export interface SensitiveRevealConfig {
  /** How to mask the placeholder value: 'partial' shows last 4 chars, 'full' hides all. */
  maskFormat?: 'partial' | 'full';
  /** Determines display formatting on reveal (DOB uses dateFormat). */
  maskType: 'SSN' | 'DOB' | 'custom';
  /** Row field used as the API lookup key. */
  idField: string;
  /** API endpoint to fetch the sensitive value. Supports path params: `/api/entity/{Id}`. */
  endpoint: string;
  /** HTTP method for the fetch. */
  method?: 'GET' | 'POST';
  /** Query param (GET) or body key (POST) sent to the API. Defaults to idField. Ignored when endpoint contains path params. */
  paramName?: string;
  /** Dot-path on the API response to extract the revealed value. Empty/omitted = use response.data directly. */
  responseField?: string;
  /** Moment format string for DOB maskType (e.g. 'MM/DD/YYYY'). */
  dateFormat?: string;
  /** Boolean row field indicating the value exists server-side. When false → show dash, no eye icon. Used when the field value itself is null but existence is known via a separate flag. */
  presenceField?: string;
  /**
   * Additional row fields sent as extra query params on the reveal fetch.
   * Keys are the query param names, values are row field names to read the value from.
   * Example: `{ companyId: 'companyId' }` → appends `&companyId=<row.companyId>` to the request.
   */
  extraRowFields?: Record<string, string>;
  /** Custom placeholder text when masked (overrides auto-generated mask). */
  placeholder?: string;
}

export interface ModalConfig {
  popoverInfo?: Record<string, unknown>;
  idField?: string;
  excludeKey?: string;
  excludeValue?: string | string[];
  enableTag?: boolean;
}

export interface FormatConfig {
  decimals?: number;
  commas?: boolean;
  fixed?: boolean;
  /**
   * Strip trailing zeros after formatting to `decimals` precision.
   * `56.00` → `56`, `56.25` → `56.25`, `56.10` → `56.1`.
   * Defaults to true for currency and percentage columns via the normalizer.
   */
  trimTrailingZeros?: boolean;
}

// ---------------------------------------------------------------------------
// Action Intent — cell click fires ActionRegistry.emitIntent()
// ---------------------------------------------------------------------------

export interface ActionIntentConfig {
  actionId: string;
  /** Maps payload keys to row field names. e.g. { rsuAwardId: '_entityId' } */
  payload: Record<string, string>;
  /** Only emit intent when cell has a non-empty value. Default: true */
  whenHasValue?: boolean;
  /**
   * 'modal'   — emits OPEN_MODAL intent (default, legacy behavior)
   * 'execute' — calls ActionRegistry.executeAction directly (for side-effect-only actions, e.g. PDF generation)
   */
  mode?: 'modal' | 'execute';
  /**
   * Static label to display as the link text, overriding the cell's field value.
   * Useful when the field is an object or when a fixed label is required (e.g. "View Tax Certificate").
   */
  label?: string;
}

// ---------------------------------------------------------------------------
// Conditional Display — declarative cell visual treatments
// ---------------------------------------------------------------------------

export type CellStyle = 'strikethrough' | 'muted' | 'bold' | 'italic' | 'highlight' | 'hidden';
export type ShowAs = 'badge' | 'tag' | 'inline';

export interface ConditionalDisplayRule {
  when: { field: string; operator: 'EXISTS' | 'NOT_EXISTS' | 'EQUAL' | 'NOT_EQUAL'; value?: unknown };
  /** Style applied to the original cell value */
  style?: CellStyle;
  /** Row field name OR literal string to show alongside/instead */
  show?: string;
  /** How to render the 'show' value. Default: 'badge' */
  as?: ShowAs;
  /** Lozenge/tag variant (only when as = 'badge' | 'tag'). Default: 'default' */
  variant?: string;
}

export type ConditionalDisplayConfig = ConditionalDisplayRule[];

// ─── Row Style Rules (declarative, JSON-serializable) ─────────────────────────

export type RowConditionOperator =
  | 'EQUAL'
  | 'NOT_EQUAL'
  | 'EXISTS'
  | 'NOT_EXISTS'
  | 'DATE_PAST'
  | 'DATE_FUTURE';

export interface RowCondition {
  field: string;
  operator: RowConditionOperator;
  value?: unknown;
}

export interface RowStyleRule {
  /** Condition(s) to evaluate. Omit for a catch-all default rule (must be last). */
  when?: RowCondition | RowCondition[];
  match?: 'all' | 'any';
  style: Record<string, string | number>;
}

export interface AggregateDef {
  fn: 'sum' | 'average' | 'count' | 'min' | 'max';
  format?: FormatConfig;
  label?: string;
}

// ---------------------------------------------------------------------------
// Table footer — table-wide footer totals
// ---------------------------------------------------------------------------

/** Client-side aggregate functions computed from visible row data. */
export type FooterAggregateFn = 'sum' | 'average' | 'count' | 'min' | 'max';

/**
 * Defines what a single footer cell displays.
 *
 * Three mutually exclusive modes:
 * - **label-only**: Just `label` — renders static text (e.g. "Total").
 * - **computed**: `aggregate` — computes value from row data client-side.
 * - **provided**: `sourceKey` — reads a pre-computed value from `footerData`.
 *
 * `label` can be combined with `aggregate` or `sourceKey` to show "Label: Value".
 */
export interface FooterColumnDef {
  /** Column field to align this footer cell under. */
  field: string;
  /** Static label text (e.g. "Total"). Renders alone or as prefix to a value. */
  label?: string;
  /** Client-side aggregate function. Computed from visible row data for this `field`. */
  aggregate?: FooterAggregateFn;
  /** Key to read a pre-computed value from the `footerData` prop (server-provided totals). */
  sourceKey?: string;
  /** Format for the computed/provided value. */
  format?: FormatConfig;
  /** Prefix prepended to the formatted value (e.g. "$"). */
  prefix?: string;
  /** Suffix appended to the formatted value (e.g. "%"). */
  suffix?: string;
}

export interface TableFooterConfig {
  columns: FooterColumnDef[];
  /** Visual style variant. 'highlighted' adds a gray background bar. Default: 'default'. */
  style?: 'default' | 'highlighted';
}

/** Inline editor configuration when `editable` is an object. */
export interface EditableDef {
  type:
    | 'text'
    | 'number'
    | 'date'
    | 'dateTime'
    | 'select'
    | 'asyncSelect'
    | 'dynamicSelect'
    | 'checkbox'
    | 'textarea'
    | 'checkboxDriven'
    | 'string';
  placeholder?: string;
  options?: Array<{ label: string; value: string | number }>;
  defaultValueField?: string;
  validation?: ValidationDef;
  /** When provided, the cell is only editable if this condition evaluates true for the row. Omit for always-editable. */
  when?: VisibilityCondition;
  /** When true, the asyncSelect/select editor allows multiple values (stored as string[]). */
  multiple?: boolean;
  width?: number | string;
  /**
   * Maximum number of decimal places allowed in a number editor.
   * Blocks further decimal input at the keystroke level (no typing past this point).
   * Omit to allow any precision.
   */
  precision?: number;
  /**
   * Row field whose value (an array) is used as the options for `select` / `asyncSelect` editors.
   * Each item must have the shape defined by `rowOptionsLabelField` / `rowOptionsValueField`.
   * Example: `rowOptionsField: 'brokers'` reads `row.brokers` as the option list.
   */
  rowOptionsField?: string;
  /** Label key on each item of the `rowOptionsField` array. Defaults to `'label'`. */
  rowOptionsLabelField?: string;
  /** Value key on each item of the `rowOptionsField` array. Defaults to `'value'`. */
  rowOptionsValueField?: string;
  /**
   * When the select value changes, also update sibling row fields.
   * Each entry maps a target row field to the key on the selected option item to read the value from.
   * Example: `[{ field: 'brokerName', fromOptionField: 'brokerCompanyName' }]`
   */
  effectToFields?: Array<{ field: string; fromOptionField: string }>;
}

export interface ValidationDef {
  type: 'string' | 'number' | 'date' | 'boolean';
  rules?: Array<{ method: string; args?: unknown[]; message?: string }>;
  /**
   * Named built-in tests. These are pre-registered custom validators:
   *   - `maxDecimalPlaces` / `exactDecimalPlaces` — decimal place constraints (params: { places?: number })
   *   - `greaterThanField` / `greaterThanOrEqualField` / `lessThanField` / `lessThanOrEqualField`
   *     `equalToField` / `notEqualToField` — cross-field numeric comparisons (params: { field: string })
   *   - `dateAfterField` / `dateAfterOrEqualField` / `dateBeforeField` / `dateBeforeOrEqualField`
   *     — cross-field date comparisons (params: { field: string })
   */
  tests?: Array<{ name: string; params?: Record<string, unknown>; message?: string }>;
  when?: Array<{
    dependsOn: string | string[];
    is?: unknown;
    then?: Array<{ method: string; args?: unknown[]; message?: string }>;
    otherwise?: Array<{ method: string; args?: unknown[]; message?: string }>;
  }>;
}

/** Async options / enrichment for a column (select editors, etc.). */
export interface DataSourceDef {
  url: string;
  method?: 'GET' | 'POST';
  mapper: { label: string; value: string };
  body?: Record<string, unknown>;
  cacheDuration?: number;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * A toolbar button with an explicit visual variant.
 * Used in the new `toolbar.buttons` API where each button self-describes its style.
 *
 * Overrides ActionDef's `variant` (`'default' | 'danger'`) with toolbar-specific values.
 */
export interface ToolbarButtonDef extends Omit<ActionDef, 'variant'> {
  /**
   * Button variant for toolbar rendering.
   * - `'primary'` — filled/accent button (typically the main CTA)
   * - `'secondary'` — outlined/muted button (default)
   * - `'danger'` — destructive action (red styling)
   * - `'ghost'` — minimal/text-only button
   *
   * Defaults to `'secondary'` when omitted.
   */
  variant?: 'primary' | 'secondary' | 'secondary-white' | 'primary-outline' | 'danger' | 'ghost';
}

/**
 * A dropdown menu group for template operations (download/import) or custom grouped actions.
 *
 * - `type: 'download'` — renders as a dropdown button; each child triggers a download action
 * - `type: 'upload'` — renders as a dropdown with file-input triggers
 * - `type: 'menu'` — renders as a generic dropdown menu (no file semantics)
 */
export interface ToolbarTemplateDef {
  label: string;
  icon?: string;
  type: 'download' | 'upload' | 'menu';
  /** DOM element id for automation/testing. Flows to the rendered element's id attribute. */
  semanticId?: string;
  children: ActionDef[];
}

/**
 * @deprecated Use `ToolbarTemplateDef`. Kept for backwards compatibility.
 */
export type ToolbarGroupDef = ToolbarTemplateDef;

export interface ActionConfig {
  row?: ActionDef[];
  bulk?: ActionDef[];
  /** Actions shown on sub-rows (both standalone sub-rows and group-by child sub-rows). */
  subRow?: ActionDef[];
  toolbar?: {
    /**
     * Ordered list of toolbar buttons. Each button declares its own `variant`.
     * Replaces the `primary` + flat-secondary pattern with a single unified list.
     */
    buttons?: ToolbarButtonDef[];
    /**
     * Dropdown menu groups for template operations (download/import) or custom grouped actions.
     * Replaces `ToolbarGroupDef` items that were mixed into `secondary`.
     */
    templates?: ToolbarTemplateDef[];

    /** @deprecated Use `buttons` with `variant: 'primary'`. */
    primary?: ActionDef;
    /** @deprecated Use `buttons` + `templates`. Flat actions go in `buttons`; groups go in `templates`. */
    secondary?: Array<ActionDef | ToolbarTemplateDef>;
  };
}

export interface ActionDef {
  id?: string;
  /** DOM element id for automation/testing. Flows to the rendered element's id attribute. */
  semanticId?: string;
  label: string;
  icon?: string;
  /** `'danger'` for destructive actions like Terminate/Delete. Defaults to `'default'`. */
  variant?: 'default' | 'danger';
  /**
   * Omit for OOP actions registered in the ActionRegistry — the registry
   * resolves execution by `id` alone.
   *
   * For **new pages with OOP action classes**, the only valid value is
   * `'startEditing'` (table-intrinsic inline-edit toggle).
   *
   * All other values are **deprecated for new pages** — their behavior
   * belongs in the action class. They remain in the type system for
   * backward compatibility with legacy tables and Schema Studio.
   */
  actionType?:
    | /** @deprecated Use action class with `emitIntent()` instead */ 'modal'
    | /** @deprecated Use action class with direct API call instead */ 'direct'
    | /** @deprecated Use action class with `getNavigate()` instead */ 'navigate'
    | /** @deprecated Use action class with `window.open()` instead */ 'newTab'
    | /** @deprecated Use action class with `downloadBlob()` instead */ 'export'
    | /** @deprecated Use action class with `pickFile()` + API instead */ 'import'
    | 'dropdown'
    | 'startEditing'
    | 'startEditingAll';
  execution?: 'api' | 'local';
  endpoint?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  payload?: Record<string, unknown>;
  path?: string;
  options?: Array<{ label: string; path?: string; actionId?: string; permission?: { action: string; resource: string } }>;
  /**
   * Router state passed to `navigate(path, { state })` for `"navigate"` actions.
   *
   * Values are processed by `resolveValue()` so both string interpolation AND
   * typed sentinel values work:
   *   - `"{row.fieldName}"` → string value of that field
   *   - `"{rowId}"` → resolved row primary key (string)
   *   - `"{selectedRowIds}"` → `string[]`  (typed, not stringified)
   *   - `"{selectAllActive}"` → `boolean`
   *   - `"{tableQuery}"` → `object`
   *
   * Example:
   *   ```json
   *   {
   *     "createdDate": "{row.createdDate}",
   *     "participantId": "{rowId}",
   *     "selectedIds": "{selectedRowIds}"
   *   }
   *   ```
   */
  state?: Record<string, unknown>;
  url?: string;
  fileName?: string;
  fileKey?: string;
  permission?: { action: string; resource: string };
  visibilityCondition?: VisibilityCondition;
  disabledWhen?: VisibilityCondition;
  /**
   * When `true`, the toolbar button is disabled until at least one row is selected.
   * Opt-in — defaults to `false` (button always enabled). Only meaningful for toolbar actions.
   */
  requiresSelection?: boolean;
  /** Shown before the action runs (confirm vs alert via `kind`). */
  dialog?: ActionDialogConfig | null;
  /** @deprecated Use `dialog`. Still read by the normalizer. */
  preflightDialog?: ActionDialogConfig | null;
  /** @deprecated Use `dialog`. Still read by the normalizer. */
  confirm?: ActionDialogConfig | null;
  onSuccess?: 'refetch' | 'toast' | 'toastAndRefetch' | 'removeRow' | 'openTab' | 'backgroundJob' | 'none';
  /**
   * For `onSuccess: "openTab"` — the URL template to open in a new tab.
   *
   * Supports both fixed strings and `{response.fieldName}` to embed a value
   * from the API response (e.g., a short-lived token returned by the server):
   *
   * ```json
   * "responseUrl": "https://app.example.com/?token={response.token}"
   * ```
   *
   * Also supports all standard row tokens (`{rowId}`, `{row.field}`, …) since
   * the URL is resolved with the full `TemplateContext`.
   */
  responseUrl?: string;
  /**
   * For `onSuccess: "backgroundJob"` — configures the modal shown when the
   * API returns `{ sentToBackground: true }`.
   */
  backgroundJobConfig?: BackgroundJobConfig;
  successMessage?: string;
  errorMessage?: string;
  /**
   * When "Select All Pages" is active, the table calls `selectAll.endpoint` instead of `endpoint`.
   *
   * You can optionally override post-success behavior **only for that path** — e.g. keep
   * `onSuccess: "toastAndRefetch"` on the action for normal multi-select, and set
   * `selectAll.onSuccess: "backgroundJob"` when the bulk-by-filter API may return
   * `{ sentToBackground: true }`.
   */
  selectAll?: {
    endpoint: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    payload?: Record<string, unknown>;
    /** When set, used instead of the action-level `onSuccess` while select-all-pages mode is active. */
    onSuccess?: OnSuccessAction;
    /** Overrides action-level `successMessage` for the select-all path only. */
    successMessage?: string;
    /** Overrides action-level `responseUrl` when `selectAll.onSuccess` is `"openTab"`. */
    responseUrl?: string;
    /**
     * Shallow-merged over action-level `backgroundJobConfig` when the effective
     * `onSuccess` is `"backgroundJob"` (from here or from the action).
     */
    backgroundJobConfig?: BackgroundJobConfig;
  };
}

/**
 * What the table does after a successful action API call:
 * - `"refetch"` — silently re-fetch the table data.
 * - `"toast"` — show a success toast only.
 * - `"toastAndRefetch"` — toast + re-fetch.
 * - `"removeRow"` — optimistically remove the acted-on row from local state.
 * - `"openTab"` — open `responseUrl` in a new browser tab.
 * - `"backgroundJob"` — the API may return `{ sentToBackground: true, taskReferenceNo }`.
 *   When `sentToBackground` is `true` the table shows the background-job modal
 *   (configured via `backgroundJobConfig`) and **then** refetches.
 *   When `sentToBackground` is `false` / absent it behaves like `"toastAndRefetch"`.
 * - `"none"` — no built-in side-effect; consumer handles everything.
 */
export type OnSuccessAction =
  | 'refetch'
  | 'toast'
  | 'toastAndRefetch'
  | 'removeRow'
  | 'openTab'
  | 'backgroundJob'
  | 'none';

export type VisibilityCondition = SimpleCondition | CompoundCondition;

export interface SimpleCondition {
  field: string;
  operator?:
    | 'EQUAL'
    | 'NOT_EQUAL'
    | 'IN'
    | 'NOT_IN'
    | 'GT'
    | 'LT'
    | 'GTE'
    | 'LTE'
    | 'TRUTHY'
    | 'FALSY';
  value?: unknown;
}

export interface CompoundCondition {
  logic: 'AND' | 'OR';
  conditions: VisibilityCondition[];
}

// ---------------------------------------------------------------------------
// Features (external)
// ---------------------------------------------------------------------------

export interface FeaturesConfig {
  /**
   * Renders a BackLink above the table toolbar.
   * `path` is the route to navigate to on click.
   */
  backLink?: {
    path: string;
    label?: string;
  };

  /**
   * Column preference persistence — presence enables the column picker and persists
   * the user's visible-column list under `persistenceKey`.
   * Absence means no column picker and no persistence.
   * The picker button is only shown when the schema declares ≥1 `optional` column.
   */
  columnPreferences?: {
    persistenceKey: string;
    reorderable?: boolean;
    resizable?: boolean;
  };

  /**
   * @deprecated Use `columnPreferences` instead.
   * Kept for backwards-compat; normalizer maps it to the new shape.
   */
  columnManagement?: {
    resizable?: boolean;
    reorderable?: boolean;
    selectorEnabled?: boolean;
    grouped?: boolean;
    persistence?: { enabled: boolean; key?: string };
  };

  pagination?: {
    defaultSize?: number;
    options?: number[];
    mode?: 'pagination' | 'loadMore' | 'infiniteScroll' | 'clientSide' | 'none';
    loadMore?: { trigger?: 'button' | 'scroll' };
    virtualScroll?: { enabled: boolean; estimatedRowHeight?: number; overscan?: number };
  };

  /**
   * Virtual scroll configuration — separate from `pagination.virtualScroll` for clarity.
   * Smart defaults kick in automatically when `pagination.mode === 'infiniteScroll'`:
   * - `enabled` → true
   * - `pageSize` → same as `pagination.defaultSize`
   * - `maxCachedPages` → 10
   * - `fetchDebounce` → 500ms
   *
   * Only declare overrides when the defaults are wrong for a specific table.
   */
  virtualScroll?: {
    enabled?: boolean;
    estimatedRowHeight?: number;
    overscan?: number;
    pageSize?: number;
    maxCachedPages?: number;
    fetchDebounce?: number;
  };

  /**
   * Presence enables the checkbox column and row selection.
   * All derived flags (showCheckbox, multiSelect, selectAllPages, bulkActionsEnabled)
   * are inferred from the schema structure — never spell them out explicitly.
   *
   * - `showCheckbox`      ← always true when selection is present
   * - `multiSelect`       ← always true (single-select uses row callbacks instead)
   * - `selectAllPages`    ← true when ≥1 bulk action has a `selectAll` endpoint OR explicitly set
   * - `bulkActionsEnabled`← true when `actions.bulk` is non-empty
   */
  selection?: {
    /** The row identifier field (e.g. "id"). Required for selection to work. */
    keyField: string;
    /** Optional row-level condition that controls whether a row is selectable. */
    selectable?: VisibilityCondition;
    /**
     * Explicitly enable "Select All Pages" even when no bulk action declares a
     * `selectAll` endpoint in the schema.  Required for OOP actions that handle
     * the select-all-vs-normal branching inside their own `execute()` method.
     */
    selectAllPages?: boolean;
  };

  search?: { enabled?: boolean; debounce?: number; placeholder?: string };

  /** Initial sort applied when the table mounts (maps to ABP `Sorting` query param). */
  defaultSort?: { field: string; direction: 'asc' | 'desc' };

  /**
   * Controls the filter panel grid layout.
   * - `columns` — number of grid columns in the filter panel (default 2 on desktop, 1 on mobile).
   */
  filterGrid?: {
    columns?: number;
  };

  rows?: {
    height?: number;
    maxHeight?: number;
    striped?: boolean;
    /** Declarative row styling rules (JSON-serializable). Evaluated in order; first match wins. Takes priority over `striped`. */
    styleRules?: RowStyleRule[];
    /** Imperative row style function. Use `styleRules` for JSON-serializable schemas. Takes priority over `striped`. */
    styleFn?: (row: Record<string, unknown>, index: number) => Record<string, string | number> | undefined;
    subRows?: SubRowConfig;
  };
  groupBy?: {
    fields: string[];
    /**
     * Controls how groups expand/collapse:
     *   - `"expanded"` — all groups open by default, user can collapse (default)
     *   - `"collapsed"` — all groups closed, user can expand
     *   - `"alwaysExpanded"` — groups always open, no toggle button
     */
    expandMode?: 'expanded' | 'collapsed' | 'alwaysExpanded';
    /** @deprecated Use `expandMode` instead. Mapped: true → "expanded", false → "collapsed". */
    defaultExpanded?: boolean;
    hideGroupedColumn?: boolean;
    /** @deprecated Not consumed. Use `expandMode: "alwaysExpanded"` to remove toggle buttons. */
    showExpandControl?: boolean;
    groupHeader?: { format?: string; showCount?: boolean; labelTemplate?: string };
    aggregates?: Record<string, AggregateDef>;
  };
  editing?: {
    bulkSave?: boolean;
    /** When true, the table hides its own edit/save/cancel UI — an external controller manages edit state via the ref handle. */
    externalControl?: boolean;
    /**
     * Schema-level row patch applied on each inline edit (before validation).
     * Return the full merged row with side-effect fields updated.
     * DynamicTableV2 prop `patchEditedRow` takes priority when both are set.
     */
    patchEditedRow?: (
      row: Record<string, unknown>,
      context: { field: string; value: unknown },
    ) => Record<string, unknown>;
    /**
     * Schema-level per-row validator; returns field → error message map (inline errors).
     * DynamicTableV2 prop `validateEditedRow` takes priority when both are set.
     */
    validateEditedRow?: (row: Record<string, unknown>) => Record<string, string>;
  };
  /** Layout overrides for embedded contexts (e.g. tables inside modals). */
  layout?: {
    /** Removes the default horizontal padding on the scroll container. Use when the parent already provides padding. */
    noPadding?: boolean;
  };
  /**
   * Shown when `dataSource.fetchPolicy` is `'requireFilter'` and no search/filter is applied.
   * Defaults to `'Search or apply filters to view results.'`.
   */
  emptyFilterMessage?: string;
  import?: { enabled: boolean; description?: string };
  columnGroups?: { renderMode: 'picker' | 'header' | 'both'; collapsible?: boolean };
  rowPinning?: {
    top?: VisibilityCondition;
    bottom?: VisibilityCondition;
  };
  /** Table-wide summary/totals row rendered as `<tfoot>`. Absent = no footer. */
  footer?: TableFooterConfig;
}

export interface SubRowConfig {
  dataField: string;
  columns: ExternalColumnDef[];
  actions?: ActionDef[];
  /**
   * Controls how sub-rows expand/collapse:
   *   - `"alwaysOpen"` — sub-rows always visible, no toggle button (default)
   *   - `"expanded"` — sub-rows start expanded, user can collapse
   *   - `"collapsed"` — sub-rows start collapsed, user expands
   *   - `"accordion"` — only one row's sub-rows visible at a time
   */
  expandMode?: 'alwaysOpen' | 'expanded' | 'collapsed' | 'accordion';
  /** @deprecated Use `expandMode` instead. Mapped for backward compatibility. */
  expand?: { defaultExpanded?: boolean; singleExpand?: boolean };
  dataSources?: DataSourceDef[];
  /** Controls sub-row left indentation: true (default) = auto-indent when room exists, false = flush. */
  indent?: boolean;
  /**
   * Sub-row columns that omit `align` inherit alignment from the parent column
   * at the corresponding visual position.  Default: true.  Set false to disable.
   */
  inheritAlignment?: boolean;
}

// ---------------------------------------------------------------------------
// Filters & compute
// ---------------------------------------------------------------------------

export interface FilterDef {
  name: string;
  /** DOM element id for automation/testing. Flows to the rendered element's id attribute. */
  semanticId?: string;
  placeholder: string;
  type: 'select' | 'asyncSelect' | 'date' | 'dateRange' | 'text' | 'number';
  options?: Array<{ label: string; value: unknown }>;
  multiple?: boolean;
  dataSource?: { url: string; labelField: string; valueField: string };
  /** Prepended to async options after fetch (legacy Toolbar additionalOption). */
  additionalOption?: { label: string; value: string | number };
  clears?: string[];
  dependsOn?: string;
  /**
   * When `type` is `dateRange`, API/query param key for the range end.
   * If omitted, defaults to `${name}End` at runtime (prefer setting explicitly).
   */
  rangeEndField?: string;
  /** Shown above the end-date picker when `type` is `dateRange`. */
  rangeEndPlaceholder?: string;
  /** Passed to the date picker (ISO or MM/DD/YYYY). */
  minDate?: string;
  maxDate?: string;
  /**
   * Number of grid columns this filter should span (1–based).
   * Defaults to 1. Use `2` to make a filter span the full row in a 2-column grid.
   */
  colSpan?: number;
}

export interface ComputeRule {
  sourceFields: string[];
  operator: 'add' | 'subtract' | 'multiply' | 'divide';
  targetField: string;
  precision?: number;
}

// ---------------------------------------------------------------------------
// Internal schema — normalized, immutable runtime shape
// ---------------------------------------------------------------------------

/**
 * Canonical table schema after normalization (version 2.0).
 *
 * @typeParam TRow — optional row model for consumers typing row data; not used in this interface body.
 */
export interface InternalTableSchema<TRow = Record<string, unknown>> {
  readonly version: '2.0';
  readonly title: string;
  readonly entity: string;
  readonly description: string;
  readonly dataSource: Readonly<InternalDataSourceConfig> | null;
  readonly columns: ReadonlyArray<InternalColumnDef>;
  readonly subRowColumns: ReadonlyArray<InternalColumnDef>;
  readonly actions: Readonly<{
    row: ReadonlyArray<InternalActionDef>;
    bulk: ReadonlyArray<InternalActionDef>;
    subRow: ReadonlyArray<InternalActionDef>;
    toolbar: Readonly<{
      /** Ordered list of toolbar buttons with per-button variant. */
      buttons: ReadonlyArray<InternalToolbarButton>;
      /** Dropdown menu groups (download/upload/menu). */
      templates: ReadonlyArray<InternalToolbarTemplate>;

      /** @deprecated Derived from `buttons` for backwards compat — first button with variant 'primary'. */
      primary: InternalToolbarButton | null;
      /** @deprecated Derived from `buttons` for backwards compat — non-primary buttons. */
      secondary: ReadonlyArray<InternalToolbarButton>;
      /** @deprecated Alias for `templates`. */
      groups: ReadonlyArray<InternalToolbarTemplate>;
    }>;
  }>;
  readonly features: Readonly<InternalFeaturesConfig>;
  readonly computeRules: ReadonlyArray<ComputeRule>;
  readonly dataInjections: ReadonlyArray<InternalDataInjection>;
  readonly subRowDataInjections: ReadonlyArray<InternalDataInjection>;
  readonly testIds: Readonly<Record<string, string>>;
}

/** Normalized column: every field present; use `null` or empty arrays where external omitted config. */
export interface InternalColumnDef {
  readonly id: string;
  readonly header: string;
  readonly field: string;
  readonly description: string;
  readonly type: ColumnType;
  readonly dataType: DataType;
  readonly width: Readonly<{ min: number; max: number; default: number }>;
  readonly align: 'left' | 'center' | 'right';
  readonly sortable: boolean;
  /** Normalised visibility — engine always sees one of these three values. */
  readonly visibility: 'locked' | 'picker' | 'hidden';
  readonly group: string | null;
  readonly emptyValue: string;
  readonly format: Readonly<FormatConfig> | null;
  readonly badge: Readonly<BadgeConfig> | null;
  readonly couple: Readonly<CoupleConfig> | null;
  readonly link: Readonly<LinkConfig> | null;
  readonly mask: Readonly<MaskConfig> | null;
  readonly sensitiveReveal: Readonly<SensitiveRevealConfig> | null;
  readonly modal: Readonly<ModalConfig> | null;
  readonly dateRange: Readonly<{ startField: string; endField: string }> | null;
  readonly conditionalValues: Readonly<Record<string, string>> | null;
  readonly fields: Readonly<Record<string, string | string[]>> | null;
  readonly editable: Readonly<InternalEditableConfig> | null;
  readonly clearOnEdit: ReadonlyArray<string>;
  readonly customRenderer: string | null;
  readonly aggregate: AggregateDef | null;
  readonly hideOnSubRows: boolean;
  readonly prefix: string | null;
  readonly uppercase: boolean;
  readonly pinned: 'left' | 'right' | null;
  readonly actionIntent: Readonly<ActionIntentConfig> | null;
  readonly conditionalDisplay: ReadonlyArray<Readonly<ConditionalDisplayRule>> | null;
}

export interface InternalEditableConfig {
  readonly type: string;
  readonly placeholder: string;
  readonly options: ReadonlyArray<{ label: string; value: string | number }>;
  readonly defaultValueField: string | null;
  readonly validation: ValidationDef | null;
  readonly when: VisibilityCondition | null;
  readonly multiple: boolean;
  readonly width: number | string | null;
  readonly precision: number | null;
  readonly rowOptionsField: string | null;
  readonly rowOptionsLabelField: string;
  readonly rowOptionsValueField: string;
  readonly effectToFields: ReadonlyArray<{ field: string; fromOptionField: string }>;
}

/** Normalized (readonly) confirmation config. */
export interface InternalConfirmConfig {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly variant: 'danger' | 'warning' | 'info';
  readonly titleAlign?: 'left' | 'center' | 'right';
  readonly messageAlign?: 'left' | 'center' | 'right';
}

export interface InternalDataSourceConfig {
  readonly endpoint: string;
  readonly method: 'GET' | 'POST';
  readonly rowIdField: string;
  readonly fetchPolicy: 'always' | 'requireFilter';
  readonly responseMapping: Readonly<{ items: string; totalCount: string; hasMoreItems: string }>;
  readonly requestParams: Readonly<Record<string, string>>;
  readonly itemsTransformId: string | null;
  readonly staticQueryParams: Readonly<Record<string, unknown>>;
  /**
   * Maps URL template tokens → context key names.
   * e.g. `{ ParticipantBrokerAccountId: 'participantBrokerAccountId' }` replaces
   * `{ParticipantBrokerAccountId}` in the endpoint with `context['participantBrokerAccountId']`.
   */
  readonly pathParams: Readonly<Record<string, string>>;
  readonly continuationCursor: Readonly<ContinuationCursorConfig> | null;
  readonly filters: ReadonlyArray<FilterDef>;
  readonly saveEndpoint: string | null;
  readonly saveMethod: 'POST' | 'PUT' | 'PATCH';
  readonly onSaveSuccess: OnSuccessAction;
  readonly saveSuccessMessage: string | null;
  readonly saveActionId: string | null;
  readonly confirmBeforeSave: InternalConfirmConfig | null;
  readonly savePayloadMapper: Readonly<Record<string, string>> | null;
}

export interface InternalFeaturesConfig {
  /**
   * Normalized column-preference config.
   * `null` means the feature is disabled (no picker, no persistence).
   * `selectorEnabled` is derived: true when persistenceKey is set AND ≥1 optional column exists.
   */
  readonly columnPreferences: Readonly<{
    persistenceKey: string;
    /** Derived: true when persistenceKey is set and ≥1 optional column exists in the schema. */
    selectorEnabled: boolean;
    /** When true, users can drag column headers to reorder. Order persists under persistenceKey. */
    reorderable: boolean;
    /** When true, users can resize columns by dragging header borders. Sizes persist under persistenceKey. */
    resizable: boolean;
  }> | null;

  readonly pagination: Readonly<{
    defaultSize: number;
    options: ReadonlyArray<number>;
    mode: 'pagination' | 'loadMore' | 'infiniteScroll' | 'clientSide' | 'none';
  }>;

  readonly selection: Readonly<{
    /** Derived: true when `selection` key is present in the external schema. */
    enabled: boolean;
    keyField: string;
    /** Derived: always true when enabled (checkboxes are what selection means in a table). */
    showCheckbox: boolean;
    /** Derived: always true when enabled (single-select uses row callbacks, not selection). */
    multiSelect: boolean;
    /** Derived: true when ≥1 bulk action has a `selectAll` endpoint. */
    selectAllPages: boolean;
    /** Derived: true when `actions.bulk` is non-empty. */
    bulkActionsEnabled: boolean;
    selectable: VisibilityCondition | null;
  }>;

  readonly search: Readonly<{ enabled: boolean; debounce: number; placeholder: string }>;
  readonly defaultSort: Readonly<{ field: string; direction: 'asc' | 'desc' }> | null;
  readonly editing: Readonly<{
    bulkSave: boolean;
    externalControl: boolean;
    patchEditedRow: ((row: Record<string, unknown>, context: { field: string; value: unknown }) => Record<string, unknown>) | null;
    validateEditedRow: ((row: Record<string, unknown>) => Record<string, string>) | null;
  }>;
  readonly groupBy: Readonly<{
    fields: ReadonlyArray<string>;
    expandMode: 'expanded' | 'collapsed' | 'alwaysExpanded';
    hideGroupedColumn: boolean;
  }> | null;
  readonly rows: Readonly<{
    height: number;
    maxHeight: number | null;
    striped: boolean;
    subRows: Readonly<InternalSubRowConfig> | null;
  }>;
  readonly virtualScroll: Readonly<{
    enabled: boolean;
    estimatedRowHeight: number;
    overscan: number;
    /** Auto-activate threshold: virtualize when row count exceeds this value */
    threshold: number;
    /** Rows per API page for random-access mode. Defaults to pagination.defaultSize. */
    pageSize: number;
    /** Max pages held in the LRU cache before eviction. Defaults to 10. */
    maxCachedPages: number;
    /** Debounce ms for page fetch requests during fast scrolling. Defaults to 500. */
    fetchDebounce: number;
  }>;
  readonly rowPinning: Readonly<{
    top: VisibilityCondition | null;
    bottom: VisibilityCondition | null;
  }> | null;
  readonly filterGrid: Readonly<{ columns: number }>;
  readonly layout: Readonly<{ noPadding: boolean }>;
  readonly emptyFilterMessage: string;
  readonly footer: Readonly<TableFooterConfig> | null;
  readonly backLink: Readonly<{ path: string; label: string }> | null;
}

export interface InternalSubRowConfig {
  readonly dataField: string;
  readonly columns: ReadonlyArray<InternalColumnDef>;
  readonly actions: ReadonlyArray<InternalActionDef>;
  readonly expandMode: 'alwaysOpen' | 'expanded' | 'collapsed' | 'accordion';
  readonly indent: boolean;
}

/** A toolbar button with resolved button variant. */
export interface InternalToolbarButton extends Omit<InternalActionDef, 'variant'> {
  readonly variant: 'primary' | 'secondary' | 'secondary-white' | 'primary-outline' | 'danger' | 'ghost';
}

/** Normalized toolbar template group — a dropdown menu containing child actions. */
export interface InternalToolbarTemplate {
  readonly label: string;
  readonly icon: string | null;
  readonly type: 'download' | 'upload' | 'menu';
  readonly semanticId: string | null;
  readonly children: ReadonlyArray<InternalActionDef>;
}

/**
 * @deprecated Use `InternalToolbarTemplate`. Kept for backwards compatibility.
 */
export type InternalToolbarGroup = InternalToolbarTemplate;

export interface InternalActionDef {
  readonly id: string;
  readonly semanticId: string | null;
  readonly label: string;
  readonly icon: string | null;
  readonly variant: 'default' | 'danger';
  readonly actionType: string | undefined;
  readonly execution: 'api' | 'local';
  readonly endpoint: string | null;
  readonly method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly payload: Readonly<Record<string, unknown>> | null;
  readonly path: string | null;
  readonly options: ReadonlyArray<{ label: string; path?: string; actionId?: string; permission?: { action: string; resource: string } }> | null;
  /**
   * Router state for `"navigate"` actions. Values are template expressions
   * resolved by `resolveValue()` at execution time.
   * String values support `{row.field}`, `{rowId}`, etc.
   * Typed sentinel values (`"{selectedRowIds}"`, `"{selectAllActive}"`, …)
   * are preserved as their native type (string[], boolean, object).
   * `null` when not specified.
   */
  readonly state: Readonly<Record<string, unknown>> | null;
  readonly url: string | null;
  readonly fileName: string | null;
  readonly fileKey: string | null;
  readonly permission: Readonly<{ action: string; resource: string }> | null;
  readonly visibilityCondition: VisibilityCondition | null;
  readonly requiresSelection: boolean;
  readonly disabledWhen: VisibilityCondition | null;
  readonly dialog: Readonly<{
    kind: ActionDialogKind;
    title: string;
    message: string;
    confirmLabel: string;
    cancelLabel: string;
    variant: 'danger' | 'warning' | 'info';
  }> | null;
  readonly onSuccess: OnSuccessAction;
  /** URL template for `onSuccess: "openTab"`. Supports `{response.field}` + all row tokens. */
  readonly responseUrl: string | null;
  /** Config for the built-in background-job modal (`onSuccess: "backgroundJob"`). */
  readonly backgroundJobConfig: Readonly<BackgroundJobConfig> | null;
  readonly successMessage: string | null;
  readonly errorMessage: string | null;
  readonly selectAll: Readonly<{
    endpoint: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    payload: Readonly<Record<string, unknown>> | null;
    /** `null` = inherit action-level `onSuccess`. */
    readonly onSuccess: OnSuccessAction | null;
    readonly successMessage: string | null;
    readonly responseUrl: string | null;
    readonly backgroundJobConfig: Readonly<BackgroundJobConfig> | null;
  }> | null;
}

export interface InternalDataInjection {
  readonly id: string;
  readonly url: string;
  readonly method: 'GET' | 'POST';
  readonly targetField: string;
  readonly mapper: Readonly<{ label: string; value: string }>;
  readonly body: Readonly<Record<string, unknown>> | null;
  readonly cacheDuration: number;
}

/** @deprecated Remove this type — AI metadata is no longer tracked on the schema. Kept temporarily so dependent code compiles without changes. */
export interface InternalAIMetadata {
  readonly purpose: string;
  readonly domain: string;
  readonly relationships: ReadonlyArray<{ entity: string; type: string; field: string }>;
  readonly keyColumns: ReadonlyArray<string>;
  readonly searchableColumns: ReadonlyArray<string>;
  readonly tags: ReadonlyArray<string>;
}

// ---------------------------------------------------------------------------
// Multi-section (accordion group) schema — external
// ---------------------------------------------------------------------------

/**
 * A section within a multi-section schema. Each section is a self-contained
 * table definition that reuses all existing column/action/feature types.
 */
export interface SectionDef {
  id: string;
  title: string;
  /** @deprecated Use top-level `sectionExpandMode` instead. Per-section override still honored. */
  defaultExpanded?: boolean;
  permission?: { action: string; resource: string };
  columns: ExternalColumnDef[];
  dataSource?: DataSourceConfig;
  actions?: ActionConfig;
  features?: FeaturesConfig;
  computeRules?: ComputeRule[];
}

/**
 * Controls the initial expand/collapse state of accordion sections.
 *
 * - `"allCollapsed"` — all sections start collapsed (default)
 * - `"allExpanded"` — all sections start expanded
 * - `"firstExpanded"` — only the first section starts expanded, rest collapsed
 * - `string[]` — array of section IDs that should start expanded
 */
export type SectionExpandMode = 'allCollapsed' | 'allExpanded' | 'firstExpanded' | string[];

/**
 * Shared page-level controls rendered above the accordion sections.
 * These apply to all sections simultaneously (e.g. a global search bar).
 */
export interface SharedControlsDef {
  search?: { enabled?: boolean; placeholder?: string; debounce?: number };
  filters?: FilterDef[];
  toolbar?: { primary?: ActionDef; secondary?: ActionDef[] };
  import?: { downloadTemplate?: ActionDef; uploadAction?: ActionDef };
  sectionExpandMode?: SectionExpandMode;
}

// ---------------------------------------------------------------------------
// Multi-section (accordion group) schema — internal (normalized)
// ---------------------------------------------------------------------------

export interface InternalMultiSectionSchema {
  readonly kind: 'multiSection';
  readonly version: '2.0';
  readonly title: string;
  readonly description: string;
  readonly sections: ReadonlyArray<InternalSection>;
  readonly sharedControls: Readonly<InternalSharedControls>;
  /** Pre-resolved set of section IDs that should be expanded on initial render. */
  readonly initialExpandedSections: ReadonlyArray<string>;
  readonly testIds: Readonly<Record<string, string>>;
}

export interface InternalSection {
  readonly id: string;
  readonly title: string;
  readonly defaultExpanded: boolean;
  readonly permission: Readonly<{ action: string; resource: string }> | null;
  readonly tableSchema: InternalTableSchema;
}

export interface InternalSharedControls {
  readonly search: Readonly<{ enabled: boolean; placeholder: string; debounce: number }>;
  readonly filters: ReadonlyArray<FilterDef>;
  readonly toolbar: Readonly<{
    primary: InternalActionDef | null;
    secondary: ReadonlyArray<InternalActionDef>;
  }>;
  readonly import: Readonly<{
    downloadTemplate: InternalActionDef | null;
    uploadAction: InternalActionDef | null;
  }>;
}
